import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'
import { Spinner } from '../../components/ui/Spinner'
import { Eye, EyeOff } from 'lucide-react'
import { useTrialDays } from '../../hooks/useTrialDays'

const schema = z.object({
  business_name: z.string().min(2, 'Business name is required'),
  owner_name: z.string().min(2, 'Your name is required'),
  email: z.string().email('Enter a valid email'),
  phone: z.string().min(10, 'Enter a valid phone number'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})
type FormData = z.infer<typeof schema>

export default function RegisterPage() {
  const navigate = useNavigate()
  const [showPass, setShowPass] = useState(false)
  const trialDays = useTrialDays()

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  async function onSubmit(values: FormData) {
    const { error } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
      options: {
        data: {
          business_name: values.business_name.trim(),
          owner_name: values.owner_name.trim(),
          phone: values.phone.trim(),
        }
      }
    })

    if (error) {
      toast.error(error.message)
      return
    }

    toast.success('Check your email to verify your account, then log in.')
    navigate('/login')
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-brand-50 to-white px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-brand-500 flex items-center justify-center shadow-lg mb-3">
            <span className="text-white font-bold text-2xl">P</span>
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900">Create your account</h1>
          <p className="text-sm text-gray-500 mt-1">Start your {trialDays}-day free trial. No credit card.</p>
        </div>

        <div className="card shadow-card-hover">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="label">Business name</label>
              <input
                type="text"
                className="input"
                placeholder="e.g. Adaeze Stores"
                {...register('business_name')}
                autoFocus
              />
              {errors.business_name && <p className="mt-1 text-xs text-red-500">{errors.business_name.message}</p>}
            </div>

            <div>
              <label className="label">Your name</label>
              <input
                type="text"
                className="input"
                placeholder="e.g. Adaeze Okonkwo"
                {...register('owner_name')}
              />
              {errors.owner_name && <p className="mt-1 text-xs text-red-500">{errors.owner_name.message}</p>}
            </div>

            <div>
              <label className="label">Work email</label>
              <input
                type="email"
                className="input"
                placeholder="you@business.com"
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>

            <div>
              <label className="label">Phone number</label>
              <input
                type="tel"
                className="input"
                placeholder="+234 800 000 0000"
                {...register('phone')}
              />
              {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>}
            </div>

            <div>
              <label className="label">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  className="input pr-10"
                  placeholder="At least 8 characters"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
            </div>

            <button type="submit" disabled={isSubmitting} className="btn-primary w-full h-11 mt-2">
              {isSubmitting ? <Spinner size="sm" /> : 'Create account →'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          By registering you agree to our terms of service.
        </p>
        <p className="text-center text-sm text-gray-500 mt-3">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-600 font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
